// According to https://formatjs.io/docs/polyfills, these polyfills are necessary to get Intl.Numberformat to work in older browsers
import '@formatjs/intl-getcanonicallocales/polyfill';
import '@formatjs/intl-locale/polyfill';
import '@formatjs/intl-pluralrules/polyfill';
import { isEmpty, padEnd, toInteger, truncate } from 'es-toolkit/compat';
// eslint-disable-next-line sort-imports-es6-autofix/sort-imports-es6
import '@formatjs/intl-pluralrules/locale-data/en'; // locale-data for en
// eslint-disable-next-line sort-imports-es6-autofix/sort-imports-es6
import '@formatjs/intl-numberformat/polyfill';
// eslint-disable-next-line sort-imports-es6-autofix/sort-imports-es6
import '@formatjs/intl-numberformat/locale-data/en'; // locale-data for en
import { useAppContext } from '@tock/coretypes';
import { useIntl } from 'react-intl';

// From https://docs.stripe.com/currencies?presentment-currency=US#zero-decimal
// as of May 31, 2024
const zeroDecimalCurrencies: string[] = [
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
];

// tslint:disable:missing-whitespace
/**
 * @param {Number} cents The value in cents
 * @param {Number} dollar The nearest dollar to round the money to
 * @returns {String} Formatted for human-readability
 */
export function displayInDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Displays in dollars except strips un-necessary decimal precision.
 * @param {Number} cents The value in cents
 * @param {Number} dollar The nearest dollar to round the money to
 * @returns {String} Formatted for human-readability
 */
export function displayInDollarsNoFixed(cents: number): number {
  return cents / 100;
}

export function toCents(dollar: number): number {
  return dollar * 100;
}

export function isDecimalCurrency(currencyCode?: string): boolean {
  if (currencyCode === undefined) {
    // previous logic assumed that if not in the list, return true
    return true;
  } else {
    return !zeroDecimalCurrencies.includes(currencyCode);
  }
}

/**
 *
 * @param {Number} amount The value, most of the time in cents.
 * @param currencyCode The currency code of the amount
 * @returns {String} Formatted for human-readability, taking in account non-decimal
 * currencies
 */
export function amountToString(amount: number, currencyCode: string): string {
  if (isDecimalCurrency(currencyCode)) {
    return displayInDollars(amount);
  } else {
    return `${amount}`;
  }
}

export function getDecimalSeparator(locale: string) {
  const numberWithDecimalSeparator = 10000.1;
  const parts = Intl.NumberFormat(locale).formatToParts(numberWithDecimalSeparator);
  const decimalSeparator = parts.find((part) => part.type === 'decimal');

  return decimalSeparator?.value ?? '.';
}

export function getGroupSeparator(locale: string) {
  const numberWithDecimalSeparator = 10000;
  const parts = Intl.NumberFormat(locale).formatToParts(numberWithDecimalSeparator);
  const groupSeparator = parts.find((part) => part.type === 'group');

  return groupSeparator?.value ?? ',';
}

/**
 * Converts from a string representation of a price to a number which is that price
 * in whole cents.
 * @param {String} priceString The price to convert
 * @returns {Number} The price in whole cents.
 */
export function stringToWholeCents(priceString: string | null, currencyCode?: string): number {
  const price = (priceString == null) || isEmpty(priceString) ? '0' : priceString;
  const splitPrice = price.split('.');
  const dollars = splitPrice[0];
  const cents = isDecimalCurrency(currencyCode)
    ? padEnd(truncate(splitPrice[1], { length: 2, omission: '' }), 2, '0')
    : '';
  return toInteger(dollars + cents);
}

export function currencyCodeToSymbol(currencyCode: string): string {
  let symbol;

  switch (currencyCode) {
    case 'USD':
    case 'CAD':
    case 'AUD':
    case 'HKD':
    case 'MXN':
    case 'SGD':
      symbol = '$';
      break;
    case 'EUR':
      symbol = '€';
      break;
    case 'DKK':
    case 'NOK':
    case 'SEK':
      symbol = 'kr.';
      break;
    case 'GBP':
      symbol = '£';
      break;
    case 'JPY':
      symbol = '¥';
      break;
    default:
      symbol = currencyCode;
  }
  return symbol;
}

export function amountCentsForCurrency(amountCents: number, currencyCode: string): number {
  return isDecimalCurrency(currencyCode) ? amountCents : Math.round(amountCents) / 100;
}

/**
 * Formats a currency in cents amounts, using a currency appropriate to the
 * current appContext.
 *
 * @param cents amount to format, in cents.
 *
 * @returns a formatted currency, or empty string if incoming cents argument was nullish.
 */
export function useFormattedCurrency(cents: number | undefined | null) {
  const {
    context: { currency },
  } = useAppContext();

  const intl = useIntl();

  if (cents === undefined || cents === null) {
    return '';
  }
  return intl.formatNumber(displayInDollarsNoFixed(cents), {
    style: 'currency',
    currency,
  });
}
